import {Vector3} from "three";

export const getPointCloudData = (imageUrl: string,
                                  setBadUrl: (error: boolean) => void,
                                  setRenderingError: (error: boolean) => void,
                                  setVertices: (vertices: any[]) => void) => {
    let pcdFileXhr: XMLHttpRequest;
    let binaryDataXhr: XMLHttpRequest;
    let headerSize: number = 0;
    let pointsCount: number = 0;
    let pcdPoints: any[];
    let averageX: number = 0;
    let averageY: number = 0;
    let averageZ: number = 0;

    const badUrlHandler = () => {
        setBadUrl(true);
        setVertices([]);
    };

    const renderingErrorHandler = () => {
        setRenderingError(true);
        setVertices([]);
    };

    const onload = () => {
        if (pcdFileXhr.readyState === 4 && pcdFileXhr.status === 200) {
            const content = pcdFileXhr.responseText;
            const allLines = content.split('\n');

            // 11 to account for the comment
            for (let i = 0; i < 11; i++) {
                parseHeader(allLines[i]);
            }

            // Retrieve point cloud file in binary format to extract the points data
            binaryDataXhr = new XMLHttpRequest();
            binaryDataXhr.open('GET', imageUrl, true);
            binaryDataXhr.responseType = 'arraybuffer';
            binaryDataXhr.onload = binaryOnload;
            binaryDataXhr.onerror = () => renderingErrorHandler();
            binaryDataXhr.send();
        }
        else {
            renderingErrorHandler();
        }
    };

    const binaryOnload = () => {
        if (binaryDataXhr.readyState === 4 && binaryDataXhr.status === 200) {
            const content = binaryDataXhr.response;
            let data;
            try {
                data = new Float32Array(content.slice(headerSize, headerSize + pointsCount * 16));
            } catch (error) {
                renderingErrorHandler();
                return;
            }

            // Use medians to apply simple filtering of outlier data points
            let allPcdPoints = [];
            for (let i = 0; i < pointsCount; i++) {
                const xVal = data[i * 4];
                const yVal = data[i * 4 + 1];
                const zVal = data[i * 4 + 2];

                if (!(isNaN(xVal) || isNaN(yVal) || isNaN(zVal))) {
                    allPcdPoints.push(new Vector3(xVal, yVal, zVal));
                }
            }
            const medianX = calculateMedian(allPcdPoints.map(point => point.x));
            const medianY = calculateMedian(allPcdPoints.map(point => point.y));
            const medianZ = calculateMedian(allPcdPoints.map(point => point.z));

            // Discard points too far from median, probably outliers
            pcdPoints = allPcdPoints.filter(point =>
                Math.abs(point.x - medianX) < 1000 &&
                Math.abs(point.y - medianY) < 1000 &&
                Math.abs(point.z - medianZ) < 1000
            );

            averageX = pcdPoints.map(point => point.x).reduce((sum, ele) => sum + ele, 0) / pcdPoints.length;
            averageY = pcdPoints.map(point => point.y).reduce((sum, ele) => sum + ele, 0) / pcdPoints.length;
            averageZ = pcdPoints.map(point => point.z).reduce((sum, ele) => sum + ele, 0) / pcdPoints.length;

            const vertices: any[] = [];
            // De-centroid the point cloud
            pcdPoints.forEach(point => {
                point.x = point.x - averageX;
                point.y = point.y - averageY;
                point.z = point.z - averageZ;
                vertices.push(point.x, point.y, point.z);
            });

            // Invoke callback to pass the evaluated vertices data back to the component
            setVertices(vertices);
        }
        else {
            renderingErrorHandler();
        }
    };

    const parseHeader = (line: string) => {
        const words = line.split(' ');
        switch (words[0]) {
            case '#':
                break;
            case 'VERSION':
                break;
            case 'FIELDS':
                break;
            case 'SIZE':
                break;
            case 'TYPE':
                break;
            case 'COUNT':
                break;
            case 'WIDTH':
                break;
            case 'HEIGHT':
                break;
            case 'VIEWPOINT':
                break;
            case 'POINTS':
                pointsCount = parseInt(words[1]);
                break;
            case 'DATA':
                break;
        }

        words.forEach(word => {headerSize = headerSize + word.length + 1});
    };

    // Retrieve point cloud file as text to evaluate the headers
    pcdFileXhr = new XMLHttpRequest();
    pcdFileXhr.open('GET', imageUrl, true);
    pcdFileXhr.onload = onload;
    pcdFileXhr.onerror = () => badUrlHandler();
    pcdFileXhr.send();
};

const calculateMedian = (numberList: number[]) => {
    const sortedNumbers = [...numberList].sort((a, b) => a - b);
    const middleIndex = Math.floor(sortedNumbers.length / 2);
    if (sortedNumbers.length % 2 === 0) {
        return (sortedNumbers[middleIndex] + sortedNumbers[middleIndex - 1]) / 2;
    }
    return sortedNumbers[middleIndex];
};